import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

/**
 * MUS8 (register MUS-CAN-08, 2026-09-05): the audio tap — what the Music
 * room's visualizer moves on. The native helper (native/audio-tap) hears the
 * playing app's output on this Mac and folds it to band energies and beats;
 * the server relays its status and frames over one streaming RPC. Nothing
 * here is audio: a frame is thirty-odd numbers, thirty times a second.
 */

/** What the tap listens to: one app by bundle id, one process, or everything on the Mac minus Cleo. */
export const AudioTapTarget = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("bundle"), bundleId: TrimmedNonEmptyString }),
  Schema.Struct({ kind: Schema.Literal("pid"), pid: NonNegativeInt }),
  Schema.Struct({ kind: Schema.Literal("system") }),
]);
export type AudioTapTarget = typeof AudioTapTarget.Type;

/** Which music source the person is on — the server maps it to a target. */
export const AudioTapSource = Schema.Literals(["spotify", "appleMusic", "system"]);
export type AudioTapSource = typeof AudioTapSource.Type;

export const AudioTapState = Schema.Literals([
  "idle",
  "starting",
  "capturing",
  "no-source",
  "permission-denied",
  "unsupported",
  "unavailable",
  "failed",
]);
export type AudioTapState = typeof AudioTapState.Type;

export const AudioTapStatus = Schema.Struct({
  state: AudioTapState,
  target: Schema.NullOr(AudioTapTarget),
  sampleRate: Schema.NullOr(Schema.Number),
  /** True while the tap is capturing but has heard only silence for a while. */
  silent: Schema.Boolean,
  /** The sentence for the person, when there is one. */
  message: Schema.NullOr(Schema.String),
});
export type AudioTapStatus = typeof AudioTapStatus.Type;

export const AudioTapFrame = Schema.Struct({
  /** Milliseconds since the capture began. */
  t: NonNegativeInt,
  /** Root-mean-square level, 0–1. */
  rms: Schema.Number,
  /** Current band energies, 0–1, low to high (thirty-two). The renderer smooths them. */
  bands: Schema.Array(Schema.Number),
  /** True on the frame an onset was detected. */
  beat: Schema.Boolean,
  /** Beats per minute once the tap has heard enough beats. */
  bpm: Schema.NullOr(Schema.Number),
});
export type AudioTapFrame = typeof AudioTapFrame.Type;

/** One event on the relay: a status change or a frame. */
export const AudioTapEvent = Schema.Union([
  Schema.Struct({ type: Schema.Literal("status"), status: AudioTapStatus }),
  Schema.Struct({ type: Schema.Literal("frame"), frame: AudioTapFrame }),
]);
export type AudioTapEvent = typeof AudioTapEvent.Type;

export const AudioTapStartInput = Schema.Struct({
  source: AudioTapSource,
});
export type AudioTapStartInput = typeof AudioTapStartInput.Type;

export class AudioTapError extends Schema.TaggedErrorClass<AudioTapError>()("AudioTapError", {
  code: Schema.Literals(["unavailable", "unsupported", "failed"]),
  reason: Schema.String,
}) {
  override get message(): string {
    return this.reason;
  }
}

export const IDLE_AUDIO_TAP_STATUS: AudioTapStatus = {
  state: "idle",
  target: null,
  sampleRate: null,
  silent: false,
  message: null,
};
